import React, { useState, useEffect, useCallback } from 'react';
import replyService, { ReplyAnalysis, InboundReply } from '../services/replyService';
import gtmService, { Offer } from '../services/gtmService';
import MarkdownView from '../components/MarkdownView';
import SavedAiDocuments from '../components/SavedAiDocuments';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui';
import { useKampagnenZuordnung, KampagneVerweis } from '../vertrieb/kampagnen';
import { routeById } from '../navigation/routes';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import RechteHinweis from '../vertrieb/RechteHinweis';

const urgencyColor = (n: number): string => (n >= 4 ? 'var(--sec-danger)' : n >= 3 ? 'var(--sec-warning)' : 'var(--sec-success)');

const ReplyHandlerPage: React.FC = () => {
  const zuordnung = useKampagnenZuordnung();
  // Plattform-Admin: auf Antworten ohne Mandant hinweisen (Clearing).
  const istPlattformAdmin = useBerechtigung().darf('admin.mandant.alle');
  const [imClearing, setImClearing] = useState(0);
  useEffect(() => {
    if (!istPlattformAdmin) return;
    Promise.resolve(replyService.getClearingAnzahl()).then((n) => setImClearing(n || 0)).catch(() => setImClearing(0));
  }, [istPlattformAdmin]);
  // Welle 2: den Posteingang (GET) lesen alle mit vertrieb.lesen; Analysieren
  // (POST /replies/analyze), Erledigt (PATCH) und Test-Eintrag (POST) brauchen
  // vertrieb.bearbeiten und fehlen ohne das Recht.
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const [reply, setReply] = useState('');
  const [offerId, setOfferId] = useState('');
  const [context, setContext] = useState('');
  const [offers, setOffers] = useState<Offer[]>([]);
  const [inbox, setInbox] = useState<InboundReply[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState<ReplyAnalysis | null>(null);
  const [raw, setRaw] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const loadOffers = useCallback(async () => {
    try {
      setOffers(await gtmService.getOffers());
    } catch {
      // optional context — ignore load errors
    }
  }, []);

  const loadInbox = useCallback(async () => {
    try {
      setInbox(await replyService.getInbox(false));
    } catch {
      // Inbox is empty/unavailable without the webhook configured — ignore.
    }
  }, []);

  useEffect(() => {
    // Angebote braucht nur das Analyseformular.
    if (darfBearbeiten) loadOffers();
    loadInbox();
  }, [darfBearbeiten, loadOffers, loadInbox]);

  const loadFromInbox = (r: InboundReply) => {
    setReply(r.text || '');
    const who = [r.firstName, r.lastName].filter(Boolean).join(' ');
    setContext([who, r.companyName, r.campaignName].filter(Boolean).join(' · '));
    setAnalysis(null);
    setRaw('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const markHandled = async (id: string) => {
    try {
      await replyService.markHandled(id, true);
      setInbox((prev) => prev.filter((x) => x.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren');
    }
  };

  const createTestReply = async () => {
    try {
      await replyService.createTestReply();
      await loadInbox();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen des Test-Eintrags');
    }
  };

  const handleAnalyze = async () => {
    if (!reply.trim()) {
      setError('Bitte die Antwort des Prospects einfügen');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setAnalysis(null);
      setRaw('');
      const result = await replyService.analyze(reply, offerId || undefined, context || undefined);
      if (result.analysis) setAnalysis(result.analysis);
      else setRaw(result.raw || 'Keine strukturierte Analyse erhalten.');
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der Analyse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <PageHeader title="Antworten einordnen (Reply Handler)" subtitle={routeById('reply-handler')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Jede Antwort ist eine Beziehungschance, keine Transaktion (powered by Skill{' '}
        <em>reply-handler</em>): Antwort einfügen → Klassifikation + 2–3 fertige, nicht-pushy
        Antwortvarianten, die zuerst Wert liefern.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      <RechteHinweis was="Antworten analysieren und als erledigt markieren" />
      {imClearing > 0 && (
        <div className="alert alert-warning" role="status">
          {imClearing === 1 ? 'Eine Lemlist-Antwort lässt' : `${imClearing} Lemlist-Antworten lassen`} sich keinem Mandanten zuordnen.{' '}
          <Link to="/antworten-clearing">Im Clearing zuweisen</Link>
        </div>
      )}

      {inbox.length === 0 && (
        <div className="alert" style={{ background: 'var(--sec-surface-alt)', color: 'var(--sec-fg-muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px' }}>
            Posteingang ist leer. Antworten erscheinen hier automatisch über den Lemlist-Webhook
            (siehe <code>WEBHOOK_SETUP.md</code>){darfBearbeiten ? ' — oder lege einen Test-Eintrag an.' : '.'}
          </span>
          {darfBearbeiten && (
            <button className="btn btn-secondary btn-sm" onClick={createTestReply} style={{ flexShrink: 0 }}>
              Test-Eintrag erzeugen
            </button>
          )}
        </div>
      )}

      {inbox.length > 0 && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-success)' }}>
          <h2 className="form-section-title">Posteingang ({inbox.length}) — via Lemlist-Webhook</h2>
          {inbox.map((r) => (
            <div
              key={r.id}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', padding: '8px 0', borderBottom: '1px solid var(--sec-border)' }}
            >
              <div style={{ fontSize: '13px', color: 'var(--sec-fg)', minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>
                  {r.email
                    ? <Link to={`/lead-sync?suche=${encodeURIComponent(r.email)}`} title="Lead in Leads & Outreach suchen">
                        {[r.firstName, r.lastName].filter(Boolean).join(' ') || r.email}
                      </Link>
                    : ([r.firstName, r.lastName].filter(Boolean).join(' ') || 'Unbekannt')}
                  {r.companyName && <> · <Link to={`/companies?suche=${encodeURIComponent(r.companyName)}`} title="Firma im CRM suchen">{r.companyName}</Link></>}
                </div>
                {(r.lemlistCampaignId || r.campaignName) && (
                  <KampagneVerweis lemlistId={r.lemlistCampaignId} name={r.campaignName} zuordnung={zuordnung} leads className="mt-1" />
                )}
                <div style={{ color: 'var(--sec-fg-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '520px' }}>
                  {r.text || '(kein Text im Webhook-Payload)'}
                </div>
              </div>
              {darfBearbeiten && (
                <div className="btn-group" style={{ flexShrink: 0 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => loadFromInbox(r)} disabled={!r.text}>
                    Analysieren
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => markHandled(r.id)}>
                    Erledigt
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {darfBearbeiten && (
      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
        <div className="form-group">
          <label className="form-label">Antwort des Prospects</label>
          <textarea
            className="form-textarea"
            rows={8}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Eingegangene Antwort hier einfügen…"
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 220px' }}>
            <label className="form-label">Angebot (GTM, optional)</label>
            <select className="form-input" value={offerId} onChange={(e) => setOfferId(e.target.value)}>
              <option value="">— keins —</option>
              {offers.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 320px' }}>
            <label className="form-label">Kontext (optional)</label>
            <input
              className="form-input"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="z.B. Branche, bisheriger Verlauf"
            />
          </div>
        </div>
        <button className="btn btn-success" onClick={handleAnalyze} disabled={loading}>
          {loading ? 'Analysiere…' : 'Antwort analysieren'}
        </button>
      </div>
      )}

      {raw && <MarkdownView content={raw} style={{ marginTop: '16px' }} />}

      {analysis && (
        <div style={{ marginTop: '20px' }}>
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="card-header">
              <div className="card-title">{analysis.category}</div>
              <span style={{ color: urgencyColor(analysis.urgency), fontWeight: 600, fontSize: '13px' }}>
                Dringlichkeit {analysis.urgency}/5
              </span>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginTop: '8px' }}>
              <div>
                <strong>Intent:</strong> {analysis.intent}
              </div>
              <div>
                <strong>Tonalität:</strong> {analysis.tone}
              </div>
              <div>
                <strong>Entscheider-Status:</strong> {analysis.decisionMaker}
              </div>
            </div>
          </div>

          {analysis.responses?.map((r, i) => (
            <div className="card" key={i} style={{ marginBottom: '12px' }}>
              <div className="card-header">
                <div className="card-title">Variante {i + 1}: {r.style}</div>
                <button className="btn btn-secondary btn-sm" onClick={() => navigator.clipboard?.writeText(r.draft)}>
                  Kopieren
                </button>
              </div>
              <MarkdownView content={r.draft} style={{ marginTop: '8px' }} />
            </div>
          ))}

          {analysis.avoid?.length > 0 && (
            <div className="alert alert-error" style={{ marginBottom: '12px' }}>
              <strong>Nicht sagen:</strong>
              <ul style={{ margin: '4px 0 0 18px' }}>
                {analysis.avoid.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          {analysis.nextSteps && (
            <div className="card">
              <div className="card-title" style={{ marginBottom: '8px' }}>Nächste Schritte</div>
              <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)' }}>
                <div>
                  <strong>Sofort:</strong> {analysis.nextSteps.immediate}
                </div>
                <div>
                  <strong>Falls keine Antwort:</strong> {analysis.nextSteps.ifNoResponse}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gespeicherte Analysen liegen im Router ai-document (Bereich Inhalte). */}
      {darf('inhalte.lesen') && <SavedAiDocuments feature="reply" reloadKey={reloadKey} />}
    </div>
  );
};

export default ReplyHandlerPage;
